import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ApiError, api } from "@/api/client";
import { BulkOwnerFinderModal } from "@/components/BulkOwnerFinderModal";
import { PublishCampaignModal } from "@/components/PublishCampaignModal";
import { CardViewSwitcher, cardGridClass, useCardView } from "@/components/CardView";
import { PageHeader } from "@/components/PageHeader";
import { usePublishProgress } from "@/components/PublishProgress";
import { Skeleton } from "@/components/Skeleton";
import { Avatar } from "@/components/Avatar";
import {
  type Filters,
  FilterBar,
  filtersToBody,
  MarketCard,
  type MarketListing,
} from "@/components/MarketShared";
import type { TeamMember } from "@/types";
import { roleLabel } from "@/types";

interface SearchOut {
  items: MarketListing[];
  total: number;
  /** ``total`` stops at the server's counting cap; when this is set the real
   *  number is higher and paging must run until a short page, not until
   *  ``total``. */
  total_capped?: boolean;
  /** False when the server abandoned counting to stay inside its time
   *  budget — ``total`` is then meaningless, so show what's loaded instead
   *  of "N of M". Paging is unaffected; it runs off the cursor. */
  total_exact?: boolean;
  limit: number;
  offset: number;
  /** Keyset cursor for the next page. Paging on this instead of ``offset``
   *  keeps page 40 as cheap as page 1 — the server seeks straight to the
   *  cursor rather than reading and discarding everything before it. Null on
   *  the last page. */
  next_cursor: string | null;
  /** Filters the live portal data can't express (amenity flags, project
   *  type, bathroom counts — none of which the scrapers extract). Shown next
   *  to the result count so the grid never quietly disagrees with the filter
   *  chips above it. */
  ignored_filters: string[];
}

interface BulkJob {
  hot_offer_id: string;
  agent_user_id: string;
  agent_name: string | null;
  job_id: string;
  job_run_id: string;
  use_ss: boolean;
  // Null at POST time — Gemini runs inside each job thread now, so the
  // resolved source ("gemini" | "fallback_source" | "fallback_empty")
  // arrives later as a ``gemini`` progress event the panel renders.
  description_source: "gemini" | "fallback_source" | "fallback_empty" | null;
  gemini_model: string | null;
}

const PAGE_SIZE = 30;
// Listings per liveness call before publishing — see the same constant in
// BulkOwnerFinderModal. Rows older than an hour are asked of the portal
// (~1-2s each), so a small chunk keeps the "verifying" state honest.
const PREFLIGHT_CHUNK = 10;

type Verdict = "live" | "gone" | "unknown";
interface PreflightResponse {
  items: { id: string; status: Verdict }[];
  live: number;
  gone: number;
  unknown: number;
}
// LocalStorage keys — bumped (filters → v2) when the saved shape
// changed: this page now uses Market's ``Filters`` type via the
// shared ``FilterBar`` component, plus three Bulk-Publish-only
// extras (textQuery / freshness / welcomesAgents) kept beside it.
const FILTER_STORAGE_KEY = "agencygrid.market_publish.filters_v2";
const PER_AGENT_STORAGE_KEY = "agencygrid.market_publish.per_agent_v1";

// Freshness shortcuts surfaced as a small pill row beside FilterBar.
// ``listed_since`` on the search body comes from this; defaults to 7d
// because anything older is rarely actionable for a same-week
// publish run.
const FRESHNESS_OPTIONS: {
  value: string;
  label: string;
  hours: number | null;
}[] = [
  { value: "", label: "Anytime", hours: null },
  { value: "24h", label: "24h", hours: 24 },
  { value: "3d", label: "3d", hours: 24 * 3 },
  { value: "7d", label: "7d", hours: 24 * 7 },
  { value: "30d", label: "30d", hours: 24 * 30 },
];

interface PageState {
  // Market's structural filters drive the FilterBar (deal/property/
  // rooms/price/neighborhoods/...). Bulk-Publish layers three extras
  // on top: ``textQuery`` (its own search input), ``freshness``
  // (recency cutoff), and ``welcomesAgents`` (a single boolean
  // surfaced as a pill — Market doesn't use it but Bulk-Publish
  // directors filter by it constantly when picking owner-direct
  // inventory).
  filters: Filters;
  textQuery: string;
  freshness: string;
  welcomesAgents: boolean;
}

const DEFAULT_STATE: PageState = {
  filters: { sort: "newest" },
  textQuery: "",
  freshness: "7d",
  welcomesAgents: false,
};

function loadStoredState(): PageState {
  if (typeof window === "undefined") return DEFAULT_STATE;
  try {
    const raw = window.localStorage.getItem(FILTER_STORAGE_KEY);
    if (!raw) return DEFAULT_STATE;
    const parsed = JSON.parse(raw);
    return {
      filters: parsed.filters ?? DEFAULT_STATE.filters,
      textQuery: parsed.textQuery ?? "",
      freshness: parsed.freshness ?? DEFAULT_STATE.freshness,
      welcomesAgents: !!parsed.welcomesAgents,
    };
  } catch {
    return DEFAULT_STATE;
  }
}
function loadStoredPerAgent(): number {
  if (typeof window === "undefined") return 5;
  try {
    const raw = window.localStorage.getItem(PER_AGENT_STORAGE_KEY);
    const n = Number(raw);
    if (Number.isFinite(n) && n >= 1 && n <= 500) return n;
  } catch {
    // fall through
  }
  return 5;
}

export function MarketPublish() {
  // Employee-chosen card layout (persisted per-browser, shared with Market /
  // Owner Listings).
  const [cardView, setCardView] = useCardView();
  const [listings, setListings] = useState<MarketListing[] | null>(null);
  const [total, setTotal] = useState(0);
  const [totalCapped, setTotalCapped] = useState(false);
  const [totalExact, setTotalExact] = useState(true);
  const [offset, setOffset] = useState(0);
  const [cursor, setCursor] = useState<string | null>(null);
  const [ignoredFilters, setIgnoredFilters] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchError, setSearchError] = useState<string | null>(null);

  const [team, setTeam] = useState<TeamMember[] | null>(null);
  const [teamError, setTeamError] = useState<string | null>(null);

  // Filter state. Composed of Market's structural ``Filters`` (driving
  // the shared FilterBar) plus three Bulk-Publish-only extras carried
  // beside it. All four are persisted to localStorage as a single
  // object so a tab reload restores the whole filter intent.
  const initialState = loadStoredState();
  const [filters, setFilters] = useState<Filters>(initialState.filters);
  const [textQuery, setTextQuery] = useState<string>(initialState.textQuery);
  const [freshness, setFreshness] = useState<string>(initialState.freshness);
  const [welcomesAgents, setWelcomesAgents] = useState<boolean>(
    initialState.welcomesAgents,
  );

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [selectedAgents, setSelectedAgents] = useState<Set<string>>(new Set());
  const [perAgent, setPerAgent] = useState<number>(loadStoredPerAgent);

  const [useSs, setUseSs] = useState(true);
  const [mhPayBalance, setMhPayBalance] = useState(true);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Outcome of the last publish when it had to skip or swap deleted rows.
  const [submitNote, setSubmitNote] = useState<string | null>(null);
  // Listing ids the preflight found gone — greyed in the grid and never
  // re-picked by the replacement step or auto-distribute.
  const [goneIds, setGoneIds] = useState<Set<string>>(new Set());
  const [verifyState, setVerifyState] = useState<{
    checked: number;
    dead: number;
    replaced: number;
    /** "unknown" verdicts swapped for a verified replacement. */
    unverified: number;
    target: number;
  } | null>(null);

  // Owner-finder scan modal — opens with the current filter snapshot
  // and runs the same algorithm as the per-agency Bulk-Find-Owners on
  // /competition/{id}, scoped by Market filters instead of one agency.
  // Smarter than the "Hide agency reposts" toggle (which only sees
  // ``owner_type='agency'`` and misses agents with unset metadata).
  const [scanOwnersOpen, setScanOwnersOpen] = useState(false);
  const [campaignOpen, setCampaignOpen] = useState(false);
  // Reopening a running/just-finished campaign after the modal was closed.
  const [resumeCampaignId, setResumeCampaignId] = useState<string | null>(null);
  const [activeCampaign, setActiveCampaign] = useState<{
    id: string;
    status: string;
    succeeded: number;
    target_count: number;
    inflight: number;
  } | null>(null);
  // Locally hide the pill for a campaign the user dismissed (reappears only
  // when a newer campaign shows up).
  const [dismissedCampaignId, setDismissedCampaignId] = useState<string | null>(
    null,
  );

  // Poll the agency's active campaign so a director who closed the modal can
  // reopen it — survives refresh and works from any device (server-side state).
  useEffect(() => {
    let cancelled = false;
    const fetchActive = async () => {
      try {
        const a = await api<typeof activeCampaign>(
          "/api/market/publish-campaign/active",
        );
        if (!cancelled) setActiveCampaign(a ?? null);
      } catch {
        /* ignore — transient */
      }
    };
    fetchActive();
    const t = window.setInterval(fetchActive, 8000);
    return () => {
      cancelled = true;
      window.clearInterval(t);
    };
  }, []);
  // Job state + per-job watching now lives in the global
  // PublishProgressContext (mounted in Layout). We only call
  // ``register`` here; the floating panel reads everything from
  // context, so no local jobs map is needed.
  const { registerBatch } = usePublishProgress();

  // Persist the four-part filter state + per-agent count whenever any
  // piece changes. One write per change rather than four separate
  // effects so a multi-field update doesn't churn localStorage.
  useEffect(() => {
    try {
      window.localStorage.setItem(
        FILTER_STORAGE_KEY,
        JSON.stringify({ filters, textQuery, freshness, welcomesAgents }),
      );
    } catch {
      /* quota / private mode */
    }
  }, [filters, textQuery, freshness, welcomesAgents]);
  useEffect(() => {
    try {
      window.localStorage.setItem(PER_AGENT_STORAGE_KEY, String(perAgent));
    } catch {
      /* ignore */
    }
  }, [perAgent]);

  // Esc clears the offer selection — director can quickly start over.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && selected.size > 0) setSelected(new Set());
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selected.size]);

  // ── data loaders ────────────────────────────────────────────────────
  // Non-FilterBar request extras — everything the scan needs that the
  // FilterBar UI doesn't express: the publishability guards, sort, free-
  // text search, and the freshness window. Kept separate from
  // ``filtersToBody`` so the owner-scan modal can layer its own live
  // FilterBar selection on top without these going stale. Deliberately
  // contains no field ``filtersToBody`` emits.
  const buildExtras = useCallback((): Record<string, unknown> => {
    // Bulk Publish always restricts to publishable inventory: never
    // exclusive to another agency, never owner-said-no-agents. The
    // Source dropdown that exposes these is hidden in the UI
    // (``hideOwnerDirect``) so the toggles stay consistent with what
    // we send.
    const extras: Record<string, unknown> = {
      exclude_exclusive: true,
      exclude_forbids_agents: true,
      sort: filters.sort || "newest",
    };
    const tq = textQuery.trim();
    if (tq) extras.text_query = tq;

    const hours =
      FRESHNESS_OPTIONS.find((f) => f.value === freshness)?.hours ?? null;
    if (hours != null) {
      extras.listed_since = new Date(
        Date.now() - hours * 3600 * 1000,
      ).toISOString();
    }

    if (welcomesAgents) extras.welcomes_agents = true;
    return extras;
  }, [filters.sort, textQuery, freshness, welcomesAgents]);

  const buildSearchBody = useCallback(
    (nextOffset: number, nextCursor?: string | null): Record<string, unknown> => {
      // Start from Market's ``filtersToBody`` so deal/property/rooms/
      // price/area/floor/year/neighborhoods/condition/building/
      // project_type are all serialized identically to the Market
      // page — single source of truth for the filter→body mapping.
      return {
        ...filtersToBody(filters),
        ...buildExtras(),
        limit: PAGE_SIZE,
        offset: nextOffset,
        // Sent together on purpose: the server prefers the cursor and
        // ignores the offset when one is present, and falls back to the
        // offset for sorts that can't be paged by keyset.
        cursor: nextCursor ?? null,
      };
    },
    [filters, buildExtras],
  );

  // ``append`` toggles the result-merge strategy: a filter change /
  // initial mount calls with ``append=false`` so the listings array is
  // replaced; ``Load more`` and the bottom-of-page IntersectionObserver
  // call with ``append=true`` so previously-loaded cards (and any
  // selections made on them) stay mounted. Without the cumulative
  // approach, picks made on page 1 silently dropped out of
  // ``orderedSelected`` the moment the user paged forward — which is
  // why bulk publish only honoured the last page's selection.
  const loadListings = useCallback(
    async (nextOffset = 0, append = false, nextCursor: string | null = null) => {
      setLoading(true);
      setSearchError(null);
      // A fresh search invalidates the cursor immediately, not when the
      // response lands: ``hasMore`` reads it, and a stale one would offer
      // "load more" pointing into the previous filter's result set.
      if (!append) setCursor(null);
      try {
        // Live portal data — ssparser + mhparser, seconds old. The frozen
        // ``/api/market/search`` still backs the Market page.
        const r = await api<SearchOut>("/api/market/live/search", {
          method: "POST",
          json: buildSearchBody(nextOffset, nextCursor),
        });
        setListings((prev) => {
          if (!append || !prev) return r.items;
          // De-dup by id when appending. Without this, a freshly-inserted
          // listing that bumps everything by one row would surface the
          // overlap as a duplicate card and double-count it in selection.
          // Still needed with keyset paging: a filter change resets the
          // cursor, and two loads can race.
          const seen = new Set(prev.map((l) => l.id));
          return [...prev, ...r.items.filter((l) => !seen.has(l.id))];
        });
        setTotal(r.total);
        setTotalCapped(!!r.total_capped);
        setTotalExact(r.total_exact !== false);
        setOffset(r.offset);
        setCursor(r.next_cursor);
        setIgnoredFilters(r.ignored_filters ?? []);
      } catch (err) {
        setSearchError(
          err instanceof ApiError ? err.message : "Failed to load market offers",
        );
        if (!append) setListings([]);
      } finally {
        setLoading(false);
      }
    },
    [buildSearchBody],
  );

  // With a capped total the count is a floor, so "are there more?" is
  // answered by the cursor (server says "here's where to resume") rather
  // than by comparing against a number that isn't the real one.
  const hasMore =
    listings != null &&
    (cursor != null ||
      (totalExact && !totalCapped && total > 0 && listings.length < total));

  const loadMore = useCallback(() => {
    if (loading || !hasMore) return;
    loadListings(offset + PAGE_SIZE, true, cursor);
  }, [loading, hasMore, offset, cursor, loadListings]);

  // Bottom-of-grid sentinel. ``rootMargin: 300px`` triggers the next
  // fetch a little before the user scrolls into the empty space, so
  // the grid feels continuous instead of pause-and-then-grow.
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) loadMore();
      },
      { rootMargin: "300px" },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [loadMore]);

  async function loadTeam() {
    try {
      const r = await api<TeamMember[]>("/api/team");
      setTeam(r.filter((m) => m.status === "active"));
    } catch (err) {
      setTeamError(
        err instanceof Error ? err.message : "Failed to load team",
      );
    }
  }

  useEffect(() => {
    loadTeam();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-run search whenever a filter changes — debounce typing in the text
  // box but fire immediately on dropdown / chip clicks.
  const filterKey = useMemo(
    () =>
      JSON.stringify({
        ...filters,
        // text changes trigger debounce, but every other field should
        // re-search instantly so the chip-click feels responsive.
      }),
    [filters],
  );
  useEffect(() => {
    const t = setTimeout(() => {
      loadListings(0);
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  // ── selection helpers ───────────────────────────────────────────────
  function toggleOne(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function toggleAllLoaded() {
    if (!listings) return;
    const ids = listings.map((l) => l.id);
    setSelected((prev) => {
      const allOn = ids.every((id) => prev.has(id));
      const next = new Set(prev);
      if (allOn) ids.forEach((id) => next.delete(id));
      else ids.forEach((id) => next.add(id));
      return next;
    });
  }
  function selectFirst(n: number) {
    if (!listings) return;
    const ids = listings
      .filter((l) => !goneIds.has(l.id))
      .slice(0, n)
      .map((l) => l.id);
    setSelected(new Set(ids));
  }
  function selectAllAgents() {
    if (!team) return;
    setSelectedAgents(new Set(team.map((m) => m.id)));
  }
  function clearAgents() {
    setSelectedAgents(new Set());
  }
  function toggleAgent(id: string) {
    setSelectedAgents((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // Sort agents by today's count ascending so the underutilised agents
  // float to the top of the picker — the load-balancing nudge that
  // makes assignment decisions effortless. Defined ahead of the
  // round-robin derivation below so ``orderedAgents`` can preserve
  // this order: the lightest agent gets offer #1, second-lightest #2,
  // etc., matching what the picker visually promises.
  const sortedTeam = useMemo(() => {
    if (!team) return null;
    return [...team].sort(
      (a, b) =>
        (a.placements_today ?? 0) - (b.placements_today ?? 0) ||
        (a.full_name || a.email).localeCompare(b.full_name || b.email),
    );
  }, [team]);

  /** "Auto-distribute" — the killer one-click flow:
   *  1. ensure agents are picked. When nothing's picked, auto-select
   *     every active agent who actually has MyHome creds + phone, in
   *     load-ascending order. Agents without creds are skipped because
   *     the backend would reject them anyway and we'd lose the whole
   *     batch on submit.
   *  2. take top ``perAgent × agentCount`` from the current listing
   *     page.
   *  3. selection is now ready to publish. The director can still
   *     uncheck individual cards before hitting the CTA. */
  function autoDistribute() {
    if (!listings || !sortedTeam) return;
    let agentIds = orderedAgents;
    if (agentIds.length === 0) {
      const credentialled = sortedTeam.filter(
        (m) => m.has_myhome_creds && m.has_myhome_phone,
      );
      if (credentialled.length === 0) return;
      agentIds = credentialled.map((m) => m.id);
      setSelectedAgents(new Set(agentIds));
    }
    const pool = listings.filter((l) => !goneIds.has(l.id));
    const want = Math.min(pool.length, perAgent * agentIds.length);
    const ids = pool.slice(0, want).map((l) => l.id);
    setSelected(new Set(ids));
  }

  // ── round-robin assignment preview ─────────────────────────────────
  const orderedSelected = useMemo(() => {
    if (!listings) return [] as string[];
    return listings.filter((l) => selected.has(l.id)).map((l) => l.id);
  }, [listings, selected]);
  const orderedAgents = useMemo(() => {
    // Iterate the load-sorted team so the round-robin's first agent is
    // the lightest one — matching what the picker shows. Falling back
    // to the natural team order would silently undo the load balancing
    // the moment auto-distribute fires.
    if (!sortedTeam) return [] as string[];
    return sortedTeam
      .filter((m) => selectedAgents.has(m.id))
      .map((m) => m.id);
  }, [sortedTeam, selectedAgents]);
  const assignmentPreview = useMemo(() => {
    if (orderedAgents.length === 0) return new Map<string, number>();
    const counts = new Map<string, number>();
    orderedSelected.forEach((_, idx) => {
      const aid = orderedAgents[idx % orderedAgents.length];
      counts.set(aid, (counts.get(aid) ?? 0) + 1);
    });
    return counts;
  }, [orderedSelected, orderedAgents]);

  // Pre-flight credential gate. The backend will 400 the entire batch
  // if any picked agent lacks MyHome creds + phone, so check up-front
  // and surface an actionable message instead of letting submit eat
  // the user's selection.
  const uncredentialled = useMemo(() => {
    if (!team) return [] as TeamMember[];
    return team.filter(
      (m) =>
        selectedAgents.has(m.id) &&
        (!m.has_myhome_creds || !m.has_myhome_phone),
    );
  }, [team, selectedAgents]);

  const canSubmit =
    selected.size > 0 &&
    selectedAgents.size > 0 &&
    uncredentialled.length === 0 &&
    !submitting;

  // ── submit ──────────────────────────────────────────────────────────
  /** Verify the selection is still on the portals, swap every deleted row
   *  for the next unselected listing in the loaded grid (same filters, same
   *  sort), then publish. Deleted rows never reach the pipeline, so a stale
   *  grid can't turn into a batch of "listing deleted" job failures. */
  async function submit() {
    if (!canSubmit || !listings) return;
    const agentIds = orderedAgents;
    setSubmitting(true);
    setSubmitError(null);
    setSubmitNote(null);

    const wanted = orderedSelected.length;
    const confirmed: string[] = [];
    const dead = new Set<string>(goneIds);
    const used = new Set<string>(orderedSelected);
    const queue = [...orderedSelected];
    let replaced = 0;
    let unverified = 0;
    // Rows whose verdict stayed "unknown" and were swapped for a replacement.
    const swapped = new Set<string>();
    let checked = 0;
    const takeReplacement = (): string | null => {
      for (const l of listings) {
        if (used.has(l.id) || dead.has(l.id)) continue;
        if (l.placement_id) continue;
        // MyHome rejects a listing without images; the server would only
        // skip it again at publish time.
        if (l.photo_count === 0) continue;
        // An "owner" the server has unmasked as an agency would only be
        // skipped again at publish time — never swap one in.
        if (l.undeclared_agency) continue;
        return l.id;
      }
      return null;
    };
    setVerifyState({ checked: 0, dead: 0, replaced: 0, unverified: 0, target: wanted });
    try {
      while (queue.length && confirmed.length < wanted) {
        const chunk = queue.splice(0, PREFLIGHT_CHUNK);
        const r = await api<PreflightResponse>("/api/market/live/preflight", {
          method: "POST",
          json: { listing_ids: chunk },
        });
        const status = new Map(r.items.map((i) => [i.id, i.status]));
        for (const id of chunk) {
          checked += 1;
          if (status.get(id) !== "gone") {
            if (status.get(id) === "live") {
              confirmed.push(id);
              continue;
            }
            // "unknown" means the portal didn't answer definitively (a half-served
            // page, a throttle, a timeout). Two such rows went to publish on
            // 2026-10-17 and failed as deleted. Swap the row for a verified
            // replacement when there is one; keep it only when there is
            // nothing else, so a probe outage can't empty the batch.
            const rep = takeReplacement();
            if (!rep) {
              confirmed.push(id);
              continue;
            }
            unverified += 1;
            swapped.add(id);
            used.add(rep);
            queue.push(rep);
            continue;
          }
          dead.add(id);
          const rep = takeReplacement();
          if (rep) {
            used.add(rep);
            queue.push(rep);
            replaced += 1;
          }
        }
        setGoneIds(new Set(dead));
        setSelected((prev) => {
          const next = new Set(prev);
          for (const id of dead) next.delete(id);
          for (const id of swapped) next.delete(id);
          for (const id of used) if (!dead.has(id) && !swapped.has(id)) next.add(id);
          return next;
        });
        setVerifyState({ checked, dead: dead.size, replaced, unverified, target: wanted });
      }
      if (confirmed.length === 0) {
        setSubmitError(
          "Every selected listing has been taken down on its portal, and nothing else is loaded to replace them. Load more or change the filters.",
        );
        return;
      }

      const r = await api<{
        jobs: BulkJob[];
        skipped_offer_ids?: string[];
        gone_offer_ids?: string[];
        agent_offer_ids?: string[];
        no_photo_offer_ids?: string[];
        batch_id: string;
      }>("/api/market/live/bulk-publish", {
        method: "POST",
        json: {
          hot_offer_ids: confirmed,
          agent_user_ids: agentIds,
          mh_pay_balance: mhPayBalance,
          use_ss: useSs,
        },
      });
      const serverGone = r.gone_offer_ids ?? [];
      for (const id of serverGone) dead.add(id);
      if (serverGone.length) setGoneIds(new Set(dead));
      const skipped = r.skipped_offer_ids?.length ?? 0;
      // "Owner" rows the server unmasked as agencies at publish time — they
      // are never published; the grid shows them with an amber badge.
      const agents = r.agent_offer_ids?.length ?? 0;
      const noPhoto = r.no_photo_offer_ids?.length ?? 0;
      if (r.jobs.length === 0) {
        setSubmitError(
          skipped > 0
            ? `All ${skipped} selected listing${skipped === 1 ? "" : "s"} are already published by the chosen agent${agentIds.length === 1 ? "" : "s"} — nothing to publish.`
            : agents > 0 && dead.size === 0
              ? `All ${agents} selected listing${agents === 1 ? "" : "s"} turned out to be posted by agencies registered as private sellers — nothing to publish.`
              : noPhoto > 0 && dead.size === 0 && agents === 0
                ? `All ${noPhoto} selected listing${noPhoto === 1 ? "" : "s"} have no photos, which MyHome requires — nothing to publish.`
                : dead.size > 0
                  ? "Every selected listing has been taken down on its portal or was posted by an agency — nothing to publish."
                  : "Nothing was published.",
        );
        return;
      }

      // Hand the whole batch to the global PublishProgressContext as one
      // aggregate — not one ``register()`` per job. A big bulk publish
      // used to spin up hundreds of individual watchers (and, once the
      // pipeline's concurrency cap left most of them queued with no
      // events yet, hundreds of concurrent status polls); the panel now
      // just tracks running/done/failed counts for the whole batch.
      registerBatch({
        batchId: r.batch_id,
        total: r.jobs.length,
        useSs,
      });
      const deadNow = dead.size - goneIds.size;
      if (deadNow > 0 || skipped > 0 || agents > 0 || unverified > 0 || noPhoto > 0) {
        setSubmitNote(
          `Publishing ${r.jobs.length} listing${r.jobs.length === 1 ? "" : "s"}` +
            (deadNow > 0
              ? ` · skipped ${deadNow} deleted listing${deadNow === 1 ? "" : "s"}${replaced ? `, replaced ${replaced}` : ""}`
              : "") +
            (unverified > 0
              ? ` · swapped ${unverified} unverified listing${unverified === 1 ? "" : "s"}`
              : "") +
            (agents > 0
              ? ` · skipped ${agents} agency listing${agents === 1 ? "" : "s"} (registered as private sellers)`
              : "") +
            (noPhoto > 0
              ? ` · skipped ${noPhoto} listing${noPhoto === 1 ? "" : "s"} without photos`
              : "") +
            (skipped ? ` · ${skipped} already published` : ""),
        );
      }

      // Reset offer selection and refresh the listings — the just-claimed
      // hot offers vanish from the agency's market view, so the director
      // immediately sees the next batch ready to assign.
      setSelected(new Set());
      loadListings(0);
    } catch (err) {
      setSubmitError(
        err instanceof ApiError ? err.message : "Bulk publish failed",
      );
    } finally {
      setSubmitting(false);
      setVerifyState(null);
    }
  }

  // ── derived render data ─────────────────────────────────────────────
  // "All loaded" rather than "all on page" — with cumulative loading the
  // notion of a current page goes away.
  const allLoadedSelected =
    listings != null &&
    listings.length > 0 &&
    listings.every((l) => selected.has(l.id));

  const agentById = useMemo(() => {
    const m = new Map<string, TeamMember>();
    (team ?? []).forEach((t) => m.set(t.id, t));
    return m;
  }, [team]);

  const recommendedTopMatches =
    listings && perAgent > 0 && orderedAgents.length > 0
      ? Math.min(listings.length, perAgent * orderedAgents.length)
      : 0;

  // ── UI ──────────────────────────────────────────────────────────────
  return (
    <div className="w-full">
      <PageHeader
        title="Bulk publish"
        subtitle="Filter the market once, distribute to your team in one click. Exclusives and 'no agents' listings are filtered out automatically."
        actions={<CardViewSwitcher value={cardView} onChange={setCardView} />}
      />

      {/* ─── STICKY ACTION BAR ───
          Always-visible at the top of the viewport so the director's
          primary action ("distribute & publish") is reachable without
          scrolling — no matter how deep into the listing grid they are. */}
      <div className="sticky top-14 lg:top-14 z-20 -mx-4 sm:-mx-6 lg:-mx-8 mb-4 px-4 sm:px-6 lg:px-8 py-3 bg-ink-950/85 backdrop-blur-md border-b border-white/5">
        <div className="card p-3 space-y-3">
          {/* row 1 — distribute controls */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/[0.03] px-2 py-1">
              <span className="text-[11px] uppercase tracking-wider text-slate-500">
                Per agent
              </span>
              <button
                type="button"
                className="h-6 w-6 rounded text-slate-300 hover:bg-white/[0.06]"
                onClick={() => setPerAgent((n) => Math.max(1, n - 1))}
                aria-label="Decrease per-agent count"
              >
                −
              </button>
              <input
                type="number"
                className="w-14 bg-transparent text-center text-sm font-semibold text-slate-100 tabular-nums focus:outline-none"
                value={perAgent}
                min={1}
                max={500}
                onChange={(e) => {
                  const n = Number(e.target.value);
                  if (Number.isFinite(n) && n >= 1 && n <= 500) setPerAgent(n);
                }}
              />
              <button
                type="button"
                className="h-6 w-6 rounded text-slate-300 hover:bg-white/[0.06]"
                onClick={() => setPerAgent((n) => Math.min(500, n + 1))}
                aria-label="Increase per-agent count"
              >
                +
              </button>
            </div>

            <button
              type="button"
              className="btn-secondary text-sm"
              onClick={autoDistribute}
              disabled={!listings || listings.length === 0 || (team?.length ?? 0) === 0}
              title={
                orderedAgents.length > 0
                  ? `Pick top ${perAgent * orderedAgents.length} offers and round-robin to ${orderedAgents.length} agent${orderedAgents.length === 1 ? "" : "s"}`
                  : `Pick top offers and round-robin across all ${team?.length ?? 0} active agents`
              }
            >
              ⚡ Auto-distribute
            </button>

            <button
              type="button"
              onClick={() => setScanOwnersOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-md bg-accent-500/10 border border-accent-500/40 hover:border-accent-500/60 text-accent-100 text-xs font-medium px-3 py-1.5 transition"
              title="Sweep the filtered market with the same dedup-graph + heuristic owner-finder used on /competition/{id}. More reliable than 'Hide agency reposts' — catches agents whose owner_type metadata is missing or stale."
            >
              🎯 Scan for owners
            </button>

            <button
              type="button"
              onClick={() => setCampaignOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-md bg-accent-500/10 border border-accent-500/40 hover:border-accent-500/60 text-accent-100 text-xs font-medium px-3 py-1.5 transition"
              title="Type a target count and let the backend scan, publish, and auto-replace deleted listings until that many actually land — no manual picking."
            >
              🎯 Publish until N
            </button>

            <button
              type="button"
              className="text-xs text-slate-400 hover:text-slate-200 px-2"
              onClick={() => selectFirst(10)}
              disabled={!listings || listings.length === 0}
            >
              Pick 10
            </button>
            <button
              type="button"
              className="text-xs text-slate-400 hover:text-slate-200 px-2"
              onClick={() => selectFirst(25)}
              disabled={!listings || listings.length === 0}
            >
              Pick 25
            </button>
            <button
              type="button"
              className="text-xs text-slate-400 hover:text-slate-200 px-2"
              onClick={toggleAllLoaded}
              disabled={!listings || listings.length === 0}
              title={
                allLoadedSelected
                  ? "Clear selection across every loaded card"
                  : `Select every loaded offer (${listings?.length ?? 0})`
              }
            >
              {allLoadedSelected ? "Unpick all" : "Pick all"}
            </button>

            <div className="ml-auto flex items-center gap-2 flex-wrap">
              <label className="text-xs text-slate-400 inline-flex items-center gap-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={mhPayBalance}
                  onChange={(e) => setMhPayBalance(e.target.checked)}
                />
                MyHome balance
              </label>
              <label className="text-xs text-slate-400 inline-flex items-center gap-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={useSs}
                  onChange={(e) => setUseSs(e.target.checked)}
                />
                Also SS.ge
              </label>
              <button
                className="btn-primary"
                disabled={!canSubmit}
                onClick={submit}
                title={
                  !canSubmit
                    ? "Pick at least one offer and one agent"
                    : `Spawn ${selected.size} pipeline jobs across ${selectedAgents.size} agent${selectedAgents.size === 1 ? "" : "s"}`
                }
              >
                {submitting
                  ? verifyState && verifyState.checked < verifyState.target
                    ? `Verifying ${verifyState.checked}/${verifyState.target}…`
                    : "Publishing…"
                  : selected.size > 0 && selectedAgents.size > 0
                    ? `Verify & publish ${selected.size} → ${selectedAgents.size}`
                    : "Publish"}
              </button>
            </div>
          </div>

          {/* row 2 — distribution preview */}
          <div className="flex items-center gap-2 flex-wrap">
            {selected.size === 0 ? (
              <span className="text-xs text-slate-500">
                {recommendedTopMatches > 0
                  ? `Auto-distribute will pick ${recommendedTopMatches} offer${recommendedTopMatches === 1 ? "" : "s"} (${perAgent} × ${orderedAgents.length} agent${orderedAgents.length === 1 ? "" : "s"}).`
                  : team?.length
                    ? `Pick agents below, then auto-distribute or hand-select.`
                    : "Loading team…"}
              </span>
            ) : selectedAgents.size === 0 ? (
              <span className="text-xs text-amber-300/80">
                {selected.size} offer{selected.size === 1 ? "" : "s"} ready —
                pick at least one agent below.
              </span>
            ) : (
              <>
                <span className="text-xs text-slate-400">
                  Round-robin →
                </span>
                {orderedAgents.map((aid) => {
                  const agent = agentById.get(aid);
                  const count = assignmentPreview.get(aid) ?? 0;
                  if (!agent) return null;
                  return (
                    <span
                      key={aid}
                      className="inline-flex items-center gap-1.5 rounded-full border border-accent-500/40 bg-accent-500/10 px-2 py-0.5 text-[11px] text-accent-100"
                    >
                      <Avatar
                        url={agent.avatar_url}
                        name={agent.full_name}
                        email={agent.email}
                        size="xs"
                        online={agent.is_online}
                      />
                      <span className="truncate max-w-[100px]">
                        {agent.full_name?.split(" ")[0] || agent.email}
                      </span>
                      <span className="font-semibold">× {count}</span>
                    </span>
                  );
                })}
                <button
                  type="button"
                  className="text-[11px] text-slate-400 hover:text-slate-200 ml-1"
                  onClick={() => setSelected(new Set())}
                  title="Clear offer selection (Esc)"
                >
                  reset offers
                </button>
              </>
            )}
          </div>

          {uncredentialled.length > 0 && (
            <div className="text-rose-300 text-xs bg-rose-500/10 border border-rose-500/25 rounded p-2">
              ⛔{" "}
              {uncredentialled.length === 1 ? "1 picked agent" : `${uncredentialled.length} picked agents`}
              {" "}
              can't be assigned —{" "}
              {uncredentialled
                .map((m) => m.full_name?.split(" ")[0] || m.email)
                .join(", ")}{" "}
              {uncredentialled.length === 1 ? "is" : "are"} missing MyHome
              creds. Save the credentials on the agent's profile, or
              deselect them, then publish.
            </div>
          )}

          {submitError && (
            <div className="text-rose-300 text-xs bg-rose-500/10 border border-rose-500/25 rounded p-2">
              {submitError}
            </div>
          )}
          {submitNote && (
            <div className="text-emerald-200 text-xs bg-emerald-500/10 border border-emerald-500/25 rounded p-2">
              ✓ {submitNote}
            </div>
          )}
        </div>
      </div>

      {/* ─── FILTER BAR ───
          Two compact rows. Row 1 is the search input; row 2 is the
          shared Market FilterBar (pill-shaped triggers + popovers)
          plus the three Bulk-Publish-only extras (freshness pills,
          welcomes-agents pill, hide-agency pill, kept under the
          FilterBar's ``Source`` dropdown which we hide via
          ``hideOwnerDirect``). Total height ≈ two card rows — no
          more half-the-page filter wall. */}
      <div className="card p-3 mb-4 space-y-2">
        {/* Search */}
        <div className="relative">
          <svg
            className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            className="input !pl-9 !py-2"
            placeholder="Search title, district, street…"
            value={textQuery}
            onChange={(e) => setTextQuery(e.target.value)}
          />
          {textQuery && (
            <button
              type="button"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-200 transition"
              onClick={() => setTextQuery("")}
              aria-label="Clear search"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>

        {/* Filter pill row — Market's FilterBar + Bulk-Publish extras */}
        <div className="flex flex-wrap items-center gap-x-1.5 gap-y-2">
          <FilterBar filters={filters} onChange={setFilters} hideOwnerDirect />

          {/* ─ Bulk-only ─ */}
          <span className="h-5 w-px bg-white/10 mx-0.5" aria-hidden />

          {/* Freshness — single pill that opens a tiny popover with
              ``Anytime / 24h / 3d / 7d / 30d``. Coalescing the five
              options into one trigger keeps the bar compact; the
              active label is shown so the current filter is legible
              at a glance. */}
          <FreshnessPill value={freshness} onChange={setFreshness} />

          {/* Welcomes-agents toggle — boolean filter rendered as a
              pill that matches FilterBar's TriggerButton style. Click
              to flip; active state is amber-tinted. */}
          <button
            type="button"
            onClick={() => setWelcomesAgents((v) => !v)}
            className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-[12px] font-medium whitespace-nowrap transition-colors ${
              welcomesAgents
                ? "bg-amber-500/15 text-amber-100 border border-amber-400/45 shadow-sm shadow-amber-500/10"
                : "bg-ink-900/60 text-slate-300 border border-white/10 hover:border-white/20 hover:text-slate-100"
            }`}
            title="Show only listings where the owner explicitly invited agencies"
          >
            🔥 Welcomes agents
          </button>

          {/* Hide-agency toggle — same pill style. Reads
              ``filters.exclude_agency`` so it survives FilterBar
              clear-all (which wipes the field). */}
          <button
            type="button"
            onClick={() =>
              setFilters({
                ...filters,
                exclude_agency: filters.exclude_agency ? undefined : true,
              })
            }
            className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-[12px] font-medium whitespace-nowrap transition-colors ${
              filters.exclude_agency
                ? "bg-emerald-500/15 text-emerald-100 border border-emerald-400/45 shadow-sm shadow-emerald-500/10"
                : "bg-ink-900/60 text-slate-300 border border-white/10 hover:border-white/20 hover:text-slate-100"
            }`}
            title="Hide agency-reposted listings — keep only owner-posted inventory"
          >
            🙅 Hide agency reposts
          </button>
        </div>
      </div>

      {/* ─── AGENT PICKER ─── */}
      <div className="card p-3 mb-4">
        <div className="flex items-center justify-between mb-2">
          <div className="label !mb-0">
            Team{" "}
            <span className="text-slate-600 font-normal">
              · sorted by today's load (lightest first)
            </span>
          </div>
          {team && team.length > 0 && (
            <div className="flex gap-1">
              <button
                type="button"
                className="text-[11px] text-slate-400 hover:text-slate-200 px-2"
                onClick={selectAllAgents}
              >
                All
              </button>
              <button
                type="button"
                className="text-[11px] text-slate-400 hover:text-slate-200 px-2"
                onClick={clearAgents}
                disabled={selectedAgents.size === 0}
              >
                None
              </button>
            </div>
          )}
        </div>
        {teamError ? (
          <div className="text-rose-300 text-xs">{teamError}</div>
        ) : sortedTeam === null ? (
          <Skeleton className="h-10" />
        ) : sortedTeam.length === 0 ? (
          <div className="text-xs text-slate-500">No active team members.</div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {sortedTeam.map((m) => {
              const on = selectedAgents.has(m.id);
              const assigned = assignmentPreview.get(m.id) ?? 0;
              const today = m.placements_today ?? 0;
              const credsOk = m.has_myhome_creds && m.has_myhome_phone;
              const credIssue = !m.has_myhome_creds
                ? "No MyHome credentials saved"
                : !m.has_myhome_phone
                  ? "MyHome credential is missing the phone number"
                  : null;
              const tipParts = [
                `Published today: ${today}`,
                m.publish_persona
                  ? `Persona set:\n${m.publish_persona}`
                  : "No persona set — generic voice will be used",
              ];
              if (credIssue)
                tipParts.push(
                  `⛔ ${credIssue}. Save creds on /team/${m.id} before assigning.`,
                );
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => toggleAgent(m.id)}
                  // Allow selecting an uncredentialled agent (so the
                  // director can spot the problem at a glance) but
                  // visually penalise the chip and surface the reason
                  // — submit is gated by ``canSubmit`` regardless.
                  className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs transition ${
                    !credsOk
                      ? on
                        ? "border-rose-500/50 bg-rose-500/10 text-rose-200"
                        : "border-rose-500/30 bg-rose-500/[0.04] text-rose-300/80 hover:border-rose-500/50"
                      : on
                        ? "border-accent-500/50 bg-accent-500/10 text-accent-200"
                        : "border-white/10 bg-white/[0.02] text-slate-300 hover:border-white/20 hover:text-slate-100"
                  }`}
                  title={tipParts.join("\n\n")}
                >
                  <Avatar
                    url={m.avatar_url}
                    name={m.full_name}
                    email={m.email}
                    size="xs"
                    online={m.is_online}
                  />
                  <span className="truncate max-w-[140px]">
                    {m.full_name || m.email}
                  </span>
                  <span className="text-[10px] uppercase tracking-wider text-slate-500">
                    {roleLabel(m.role)}
                  </span>
                  <span
                    className={`text-[10px] tabular-nums px-1.5 py-0.5 rounded-full ${
                      today === 0
                        ? "bg-emerald-500/15 text-emerald-300"
                        : today < 5
                          ? "bg-white/[0.06] text-slate-300"
                          : "bg-amber-500/15 text-amber-300"
                    }`}
                    title={`${today} placement${today === 1 ? "" : "s"} today`}
                  >
                    {today}
                  </span>
                  {on && assigned > 0 && (
                    <span className="text-[10px] font-semibold text-accent-300">
                      ×{assigned}
                    </span>
                  )}
                  {!credsOk && (
                    <span
                      className="text-[10px] text-rose-300"
                      title={credIssue ?? undefined}
                    >
                      ⛔
                    </span>
                  )}
                  {credsOk && !m.publish_persona && (
                    <span
                      className="text-[10px] text-amber-300/80"
                      title="No persona set"
                    >
                      ⚠
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* ── listings grid ── */}
      {searchError && (
        <div className="card p-4 border-rose-500/25 bg-rose-500/[0.04] text-rose-300 text-sm mb-4">
          {searchError}
        </div>
      )}

      <div className="flex items-center justify-between mb-3">
        <div className="text-xs text-slate-500 tabular-nums">
          {loading
            ? "loading…"
            : totalExact
              ? `${listings?.length ?? 0} of ${total.toLocaleString()}${
                  totalCapped ? "+" : ""
                } matching · ${selected.size} picked`
              : `${listings?.length ?? 0} loaded · ${selected.size} picked`}
        </div>
      </div>

      {/* Filters the portal data can't answer. Saying so beats returning a
          grid that silently ignores a chip the user can still see set. */}
      {ignoredFilters.length > 0 && (
        <div className="card p-3 border-amber-500/25 bg-amber-500/[0.04] text-amber-200/90 text-xs mb-3">
          <span className="font-medium">Not applied to live portal data:</span>{" "}
          {ignoredFilters.join("; ")}
        </div>
      )}

      {loading && listings === null ? (
        <div className={cardGridClass(cardView)}>
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-[360px]" />
          ))}
        </div>
      ) : listings && listings.length === 0 ? (
        <div className="card p-8 text-center text-slate-500 text-sm">
          No matching offers. Loosen the filters and try again.
        </div>
      ) : (
        <div className={cardGridClass(cardView)}>
          {listings?.map((l) =>
            goneIds.has(l.id) ? (
              // Verified gone on its portal during a publish — kept in the
              // grid (the parser still has the row) but greyed and
              // unselectable, so the next pick doesn't walk into it again.
              <div
                key={l.id}
                className="relative opacity-55 grayscale pointer-events-none"
                title="This listing is no longer on its portal — skipped"
              >
                <span className="absolute top-2 left-2 z-10 text-[10px] uppercase tracking-wider text-rose-200 bg-rose-500/20 border border-rose-500/40 rounded px-1.5 py-0.5">
                  Deleted
                </span>
                <MarketCard listing={l} variant={cardView} />
              </div>
            ) : (
              <MarketCard
                key={l.id}
                listing={l}
                variant={cardView}
                selectable
                selected={selected.has(l.id)}
                onToggleSelect={() => toggleOne(l.id)}
              />
            ),
          )}
        </div>
      )}

      {/* ─── INFINITE SCROLL ───
          Sentinel triggers ``loadMore`` via IntersectionObserver as the
          user nears the bottom of the grid; the explicit "Load more"
          button is a fallback for keyboard / no-IO browsers and a
          visible affordance so the user never has to guess whether
          more pages exist. */}
      {listings && listings.length > 0 && hasMore && (
        <>
          <div ref={sentinelRef} aria-hidden className="h-1" />
          <div className="flex flex-col items-center mt-4 mb-8 gap-2">
            <button
              type="button"
              className="btn-secondary text-xs"
              disabled={loading}
              onClick={loadMore}
            >
              {loading
                ? "Loading…"
                : totalCapped || !totalExact
                  ? "Load more"
                  : `Load more (${(total - listings.length).toLocaleString()} left)`}
            </button>
            {loading && (
              <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 w-full">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} className="h-[360px]" />
                ))}
              </div>
            )}
          </div>
        </>
      )}
      {listings && listings.length > 0 && !hasMore && (
        <div className="text-center text-xs text-slate-500 mt-4 mb-8">
          You've reached the end · {(listings?.length ?? 0).toLocaleString()}{" "}
          offers loaded
        </div>
      )}

      {scanOwnersOpen && (
        <BulkOwnerFinderModal
          source={{
            kind: "market",
            // Seed the in-modal FilterBar with the page's current property
            // filters; the modal owns them from here. The page-specific
            // extras (text/freshness/sort/exclude_*) ride along as the
            // base body underneath the modal's live FilterBar selection.
            initialFilters: filters,
            baseBody: buildExtras(),
            label: (() => {
              const bits: string[] = [];
              if (filters.deal_type) bits.push(filters.deal_type);
              if (filters.neighborhoods?.length)
                bits.push(`${filters.neighborhoods.length} neighborhood${filters.neighborhoods.length === 1 ? "" : "s"}`);
              if (freshness)
                bits.push(
                  FRESHNESS_OPTIONS.find((f) => f.value === freshness)?.label ??
                    freshness,
                );
              const tq = textQuery.trim();
              if (tq) bits.push(`"${tq.slice(0, 30)}${tq.length > 30 ? "…" : ""}"`);
              return bits.length > 0
                ? `Filtered market · ${bits.join(" · ")}`
                : "Filtered market";
            })(),
          }}
          onClose={() => setScanOwnersOpen(false)}
        />
      )}

      {campaignOpen && (
        <PublishCampaignModal
          initialFilters={filters}
          baseBody={buildExtras()}
          label={(() => {
            const bits: string[] = [];
            if (filters.deal_type) bits.push(filters.deal_type);
            if (filters.neighborhoods?.length)
              bits.push(
                `${filters.neighborhoods.length} neighborhood${filters.neighborhoods.length === 1 ? "" : "s"}`,
              );
            return bits.length > 0
              ? `Filtered market · ${bits.join(" · ")}`
              : "Filtered market";
          })()}
          onClose={() => setCampaignOpen(false)}
        />
      )}

      {resumeCampaignId && (
        <PublishCampaignModal
          resumeCampaignId={resumeCampaignId}
          onClose={() => setResumeCampaignId(null)}
        />
      )}

      {/* Floating "View campaign" pill — lets the director reopen a running (or
          just-finished) campaign after closing the modal. */}
      {activeCampaign &&
        activeCampaign.id !== dismissedCampaignId &&
        !campaignOpen &&
        resumeCampaignId === null && (
          <div className="fixed bottom-4 right-4 z-[1200] flex items-center gap-2 rounded-full border border-accent-500/40 bg-ink-950/95 shadow-2xl backdrop-blur px-3 py-2">
            <span
              className={`inline-block w-2 h-2 rounded-full ${
                activeCampaign.status === "running"
                  ? "bg-accent-400 animate-pulse"
                  : "bg-slate-500"
              }`}
            />
            <button
              type="button"
              onClick={() => setResumeCampaignId(activeCampaign.id)}
              className="text-xs text-slate-200 hover:text-white font-medium"
              title="Reopen the publish campaign progress"
            >
              Publish campaign · {activeCampaign.succeeded}/
              {activeCampaign.target_count} landed
              {activeCampaign.status !== "running"
                ? ` · ${activeCampaign.status.replace("_", " ")}`
                : activeCampaign.inflight > 0
                  ? ` · ${activeCampaign.inflight} in flight`
                  : ""}{" "}
              — View
            </button>
            <button
              type="button"
              onClick={() => setDismissedCampaignId(activeCampaign.id)}
              className="text-slate-500 hover:text-slate-300 text-sm leading-none"
              title="Hide"
            >
              ✕
            </button>
          </div>
        )}
    </div>
  );
}

/** ``Listed within X`` pill that mirrors Market FilterBar's
 *  TriggerButton + Popover style. Coalesces five preset windows into
 *  a single trigger so the bar stays compact; the active label is
 *  shown on the trigger so the current filter is legible at a glance. */
function FreshnessPill({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const active = value !== "" && value != null;
  const label =
    FRESHNESS_OPTIONS.find((o) => o.value === value)?.label ?? "Anytime";
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-[12px] font-medium whitespace-nowrap transition-colors ${
          active
            ? "bg-accent-500/15 text-accent-100 border border-accent-500/40 shadow-sm shadow-accent-500/10"
            : "bg-ink-900/60 text-slate-300 border border-white/10 hover:border-white/20 hover:text-slate-100"
        }`}
      >
        <span>Listed{active ? `: ${label}` : ""}</span>
        <svg
          width="10"
          height="10"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={`transition-transform ${open ? "rotate-180" : ""} ${
            active ? "opacity-80" : "opacity-50"
          }`}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && (
        <>
          <div
            className="fixed inset-0 z-[1100]"
            onClick={() => setOpen(false)}
          />
          <div className="absolute z-[1200] mt-1 left-0 min-w-[10rem] rounded-lg bg-ink-900 border border-white/10 shadow-xl p-2 flex flex-col gap-0.5">
            {FRESHNESS_OPTIONS.map((opt) => {
              const on = opt.value === value;
              return (
                <button
                  key={opt.value || "any"}
                  type="button"
                  onClick={() => {
                    onChange(opt.value);
                    setOpen(false);
                  }}
                  className={`flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-md text-[12px] text-left transition-colors ${
                    on
                      ? "bg-accent-500/15 text-accent-100"
                      : "text-slate-300 hover:bg-white/[0.04] hover:text-slate-100"
                  }`}
                >
                  <span>{opt.label}</span>
                  {on && (
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="text-accent-300"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

